import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ProductResponseDTO, PublicProductResponseDTO } from '@libs/dtos/product/product.dto';
import { AuthService } from '@services/auth/auth.service';
import { CurrentUserService } from '@services/auth/current-user.service';
import { BrandingInfo, BrandingService } from '@services/branding/branding.service';
import { CheckoutOrder, OrderCheckoutService } from '@services/checkout/order-checkout.service';
import { PricingService } from '@services/checkout/pricing.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import {
	CryptoPaymentMethod,
	ManualPaymentConfirmed,
	ManualPaymentSelected,
	PaymentMethod,
	PaymentMethodModalComponent,
} from '@shared/components/payment-method-modal/payment-method-modal.component';
import { ProductCatalogService } from '@shared/components/product-picker/product-catalog.service';
import { ProductPickerComponent } from '@shared/components/product-picker/product-picker.component';
import { SharedModule } from '@shared/modules/shared.module';
import { AuthModalService } from '@shared/services/auth-modal.service';
import { BRANDING } from 'app/core/constants/branding.constants';
import { platformLinkPlaceholder } from 'app/core/constants/platform-brand.constants';
import { debounceTime, distinctUntilChanged, filter, Subject, takeUntil } from 'rxjs';

const BOOST_FORM_STORAGE_KEY = 'boostFormData';

/** Whole units only: no decimals, no signs (parseInt used to truncate them silently) */
const WHOLE_NUMBER_PATTERN = /^\d+$/;

/** Form data kept in sessionStorage while an anonymous user logs in */
interface SavedBoostForm {
	platform?: string;
	product?: string;
	quantity?: string | number;
	link?: string;
	customComments?: string;
}

@Component({
	selector: 'app-boost',
	imports: [SharedModule, InputComponent, ButtonComponent, PaymentMethodModalComponent, ProductPickerComponent],
	templateUrl: './boost.component.html',
	styleUrl: './boost.component.scss',
	// Catalog, price and order flows of this form (one set per page)
	providers: [ProductCatalogService, PricingService, OrderCheckoutService],
})
export class BoostComponent implements OnInit, OnDestroy {
	@ViewChild('paymentModal') paymentModal!: PaymentMethodModalComponent;
	private static warningShown = false;

	boostForm: FormGroup;

	// Authentication state
	isAuthenticated = false;

	// Destroy subject for cleanup
	private destroy$ = new Subject<void>();

	/** Platform → product → details of the form */
	readonly catalog = inject(ProductCatalogService);
	/** Price from the API, volume discount and promo code */
	readonly pricing = inject(PricingService);
	/** Order creation for each payment path */
	readonly checkout = inject(OrderCheckoutService);

	// Custom comments state
	showCommentsDialog = false;
	customComments = '';

	// Payment modal state
	showPaymentModal = false;

	/** Form saved before login, restored step by step as platforms and products load */
	private pendingRestore: SavedBoostForm | null = null;

	// Branding info for background image
	branding: BrandingInfo | null = null;
	// Public header: same brand as the client topbar (monkey logo + wordmark)
	readonly brandLogo = BRANDING.logo;
	readonly brandWordmark = BRANDING.topbarWordmark;

	constructor(
		private fb: FormBuilder,
		private router: Router,
		private authService: AuthService,
		private currentUserService: CurrentUserService,
		private messageAlertService: MessageAlertService,
		private brandingService: BrandingService,
		private authModalService: AuthModalService,
		private translocoService: TranslocoService,
		@Inject(PLATFORM_ID) private platformId: object,
	) {
		this.boostForm = this.fb.group({
			platform: ['', Validators.required],
			product: ['', Validators.required],
			quantity: ['', [Validators.required, Validators.min(1), Validators.pattern(WHOLE_NUMBER_PATTERN)]],
			link: ['', [Validators.required, Validators.pattern(/^https?:\/\/.+/)]],
		});

		// Watch for quantity changes to recalculate price with debounce
		this.boostForm
			.get('quantity')
			?.valueChanges.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				filter(() => !!this.selectedProductDetails && this.isAuthenticated),
				takeUntil(this.destroy$),
			)
			.subscribe((quantity) => {
				const num = Number(quantity);
				if (quantity !== '' && quantity !== null && Number.isInteger(num) && num > 0) {
					this.calculateDynamicPrice(num);
				} else {
					this.pricing.clear(); // no total for an empty or invalid quantity
				}
			});
	}

	ngOnInit() {
		// Check authentication status
		this.isAuthenticated = this.authService.isAuthenticated();

		this.connectCatalog();

		// Only show warning and restore data for authenticated users (browser only to avoid SSR duplicate).
		// The saved form is read before the platforms load: they drive the restore
		if (this.isAuthenticated && isPlatformBrowser(this.platformId)) {
			if (!BoostComponent.warningShown && !this.currentUserService.hasRole('admin')) {
				BoostComponent.warningShown = true;
				this.showPublicAccountWarning();
			}
			this.pendingRestore = this.readSavedFormData();
		}

		// Load branding first, then platforms
		this.brandingService
			.load()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (branding) => {
					this.branding = branding;
					this.catalog.loadPlatforms();
				},
				error: () => {
					// Load platforms anyway on error
					this.catalog.loadPlatforms();
				},
			});
	}

	get backgroundImage(): string | null {
		return this.branding?.boostBackgroundImage || null;
	}

	private showPublicAccountWarning() {
		this.messageAlertService.showMessage(
			'info',
			this.translocoService.translate('boost.messages.publicAccountTitle'),
			this.translocoService.translate('boost.messages.publicAccountMessage'),
		);
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/** Public or authenticated catalog; the saved form (after login) is restored as it loads */
	private connectCatalog() {
		this.catalog.connect(
			this.boostForm,
			{
				publicCatalog: !this.isAuthenticated,
				lockWhileLoading: true,
				// Everything tied to the previous platform goes: quantity, comments, price, promo
				onPlatformChange: () => this.resetOrderDetails(),
				onProductLoaded: () => {
					// Update form validators based on product limits
					this.updateQuantityValidators();

					// Recalculate price if quantity already exists and user is authenticated
					if (this.isAuthenticated) {
						const currentQuantity = this.quantity; // comments count for custom-comments products
						if (currentQuantity) {
							this.calculateDynamicPrice(currentQuantity);
						}
					}
				},
				onPlatformsLoaded: () => {
					// Saved form first (if its platform still exists), otherwise the first platform (Instagram)
					const platforms = this.catalog.platforms;
					const restorePlatform = this.pendingRestore?.platform;
					if (restorePlatform && platforms.some((p) => p.value === restorePlatform)) {
						this.boostForm.patchValue({ platform: restorePlatform });
					} else {
						this.applyRestoredFields(); // platform gone: keep at least link/quantity/comments
						if (platforms.length > 0 && !this.boostForm.get('platform')?.value) {
							this.boostForm.patchValue({ platform: platforms[0].value });
						}
					}
				},
				onPlatformsError: () => {
					this.pendingRestore = null;
				},
				onProductsLoaded: (platformId) => this.restoreProductSelection(platformId),
				onProductsError: (platformId) => {
					if (this.pendingRestore?.platform === platformId) this.applyRestoredFields();
				},
			},
			this.destroy$,
		);
	}

	// Catalog state used by the form and the summary
	get selectedPlatform(): string {
		return this.catalog.selectedPlatform;
	}

	get selectedProduct(): string {
		return this.catalog.selectedProduct;
	}

	get selectedProductDetails(): ProductResponseDTO | PublicProductResponseDTO | null {
		return this.catalog.selectedProductDetails;
	}

	private updateQuantityValidators() {
		const quantityControl = this.boostForm.get('quantity');
		if (!quantityControl || !this.selectedProductDetails) return;

		// For custom comments products, quantity is not required in the form
		if (this.requiresCustomComments) {
			quantityControl.clearValidators();
			quantityControl.updateValueAndValidity();
			return;
		}

		// For regular products, apply standard validators (whole units only)
		const validators = [Validators.required, Validators.min(1), Validators.pattern(WHOLE_NUMBER_PATTERN)];

		if (this.selectedProductDetails.minAmount) {
			validators.push(Validators.min(this.selectedProductDetails.minAmount));
		}
		if (this.selectedProductDetails.maxAmount) {
			validators.push(Validators.max(this.selectedProductDetails.maxAmount));
		}

		quantityControl.setValidators(validators);
		quantityControl.updateValueAndValidity();
	}

	async onSubmit() {
		// If not authenticated, save form data and redirect to login
		if (!this.isAuthenticated) {
			this.saveFormDataToSession();
			this.router.navigate(['/login'], {
				queryParams: { returnUrl: '/' },
			});
			return;
		}

		if (this.canSubmitForm && !this.checkout.creatingOrder) {
			const currentUser = this.currentUserService.getCurrentUser();

			if (!currentUser?.id) {
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('boost.messages.notAuthenticated'),
				);
				return;
			}

			const formData = this.boostForm.value;

			if (this.requiresCustomComments) {
				const comments = this.getValidComments();
				if (comments.length === 0) {
					this.messageAlertService.showMessage(
						'warn',
						this.translocoService.translate('common.labels.warning'),
						this.translocoService.translate('boost.messages.commentRequiredProduct'),
					);
					return;
				}
			}

			const quantity = this.requiresCustomComments ? this.getCommentsCount() : Number(formData.quantity);

			const orderData: CheckoutOrder = {
				userId: currentUser.userId.toString(),
				platformId: this.selectedPlatform,
				productId: this.selectedProduct,
				amount: quantity,
				url: formData.link,
				...(this.requiresCustomComments && { customComments: this.customComments }),
				// the promo code shown as applied must also reach the payment (the price is recomputed server-side)
				...(this.pricing.promoCodeApplied && this.pricing.promoCode && { promoCode: this.pricing.promoCode }),
			};

			// Admin and Root create the order directly (no payment)
			const isAdmin = this.currentUserService.hasPermissionOrAdmin('orders:create-direct');

			if (isAdmin) {
				await this.checkout.createDirect(orderData, () => this.resetForm());
			} else if (this.pricing.price <= 0) {
				// No price from the API yet (or it failed): the payment would show a total of 0
				this.pricing.priceError = true;
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('common.states.priceError'),
				);
			} else {
				// Regular users: show the payment method modal
				this.checkout.pendingOrderData = orderData;
				this.showPaymentModal = true;
			}
		} else {
			if (!this.canSubmitForm) {
				this.markFormGroupTouched();
				if (this.requiresCustomComments && this.getCommentsCount() === 0) {
					this.messageAlertService.showMessage(
						'warn',
						this.translocoService.translate('common.labels.warning'),
						this.translocoService.translate('boost.messages.commentRequiredProduct'),
					);
				} else {
					this.messageAlertService.showMessage(
						'warn',
						this.translocoService.translate('common.labels.warning'),
						this.translocoService.translate('boost.messages.fillRequired'),
					);
				}
			}
		}
	}

	private saveFormDataToSession(): void {
		const formData = {
			platform: this.selectedPlatform,
			product: this.selectedProduct,
			quantity: this.boostForm.get('quantity')?.value,
			link: this.boostForm.get('link')?.value,
			customComments: this.customComments,
		};
		sessionStorage.setItem(BOOST_FORM_STORAGE_KEY, JSON.stringify(formData));
	}

	/** Reads (and clears) the form saved before login; it is applied as platforms and products load */
	private readSavedFormData(): SavedBoostForm | null {
		const savedData = sessionStorage.getItem(BOOST_FORM_STORAGE_KEY);
		if (!savedData) return null;
		sessionStorage.removeItem(BOOST_FORM_STORAGE_KEY);
		try {
			return JSON.parse(savedData) as SavedBoostForm;
		} catch (e) {
			console.error('Error restoring form data:', e);
			return null;
		}
	}

	/** Products of the restored platform are in: select the saved product only if it is still offered */
	private restoreProductSelection(platformId: string): void {
		const data = this.pendingRestore;
		if (!data || data.platform !== platformId) return;
		if (data.product && this.catalog.products.some((p) => p.value === data.product)) {
			this.boostForm.patchValue({ product: data.product });
		}
		this.applyRestoredFields();
	}

	/** Last restore step: quantity, link and comments (set after the platform/product resets) */
	private applyRestoredFields(): void {
		const data = this.pendingRestore;
		this.pendingRestore = null;
		if (!data) return;
		if (data.quantity) this.boostForm.patchValue({ quantity: data.quantity });
		if (data.link) this.boostForm.patchValue({ link: data.link });
		if (data.customComments) this.onCommentsChange(data.customComments);
	}

	async onPaymentMethodSelected(method: PaymentMethod) {
		if (!this.checkout.pendingOrderData) return;

		if (method === 'card') {
			await this.checkout.payByCard(this.checkout.pendingOrderData);
			this.checkout.pendingOrderData = null;
		}
		// For Revolut and Bizum the modal drives the flow through manualPaymentSelected
	}

	async onCryptoPaymentSelected(event: { method: CryptoPaymentMethod }) {
		await this.checkout.createCrypto(
			event.method,
			(orderId) => this.onFreeOrderPaid(orderId),
			(response) => this.paymentModal.showCryptoInstructions(response),
		);
	}

	async onManualPaymentSelected(event: ManualPaymentSelected) {
		await this.checkout.createPending(event.method, (orderId) => this.onFreeOrderPaid(orderId));
	}

	async onManualPaymentConfirmed(event: ManualPaymentConfirmed) {
		if (!event.orderId) return;

		await this.checkout.confirmManualPayment(event.orderId, () => {
			this.resetForm();
			this.checkout.resetPaymentState();
		});
	}

	onPaymentModalCancelled() {
		this.checkout.cancel();
	}

	/** Total 0 €: the order is already paid and sent, so no payment instructions are shown */
	private onFreeOrderPaid(orderId: number): void {
		this.paymentModal.close();
		this.messageAlertService.showMessage(
			'success',
			this.translocoService.translate('boost.messages.orderCreatedTitle'),
			this.translocoService.translate('boost.messages.freeOrderSent', { orderId }),
		);
		this.resetForm();
		this.checkout.resetPaymentState();
		this.router.navigate(['/orders']);
	}

	private resetForm() {
		this.boostForm.patchValue({ link: '' });
		this.boostForm.get('link')?.markAsUntouched();
		this.boostForm.get('link')?.markAsPristine();
		this.resetOrderDetails();
	}

	/** Quantity, comments, price and promo: they belong to one product, never carried to another */
	private resetOrderDetails() {
		this.boostForm.patchValue({ quantity: '' });
		this.boostForm.get('quantity')?.markAsUntouched();
		this.boostForm.get('quantity')?.markAsPristine();
		this.customComments = '';
		this.pricing.clearPromo();
		this.pricing.clear();
	}

	private markFormGroupTouched() {
		for (const key of Object.keys(this.boostForm.controls)) {
			const control = this.boostForm.get(key);
			control?.markAsTouched();
		}
	}

	isFieldInvalid(field: string): boolean {
		const control = this.boostForm.get(field);
		return !!(control?.invalid && control?.touched);
	}

	getFieldError(field: string): string {
		const control = this.boostForm.get(field);
		if (!control?.errors || !control.touched) return '';
		if (control.errors['required']) return this.translocoService.translate('boost.errors.required');
		if (control.errors['pattern'])
			return this.translocoService.translate(
				field === 'quantity' ? 'validation.integer' : 'boost.errors.invalidUrl',
			);
		if (control.errors['min'])
			return this.translocoService.translate('boost.errors.minUnits', { min: control.errors['min'].min });
		if (control.errors['max'])
			return this.translocoService.translate('boost.errors.maxUnits', { max: control.errors['max'].max });
		return this.translocoService.translate('boost.errors.invalidField');
	}

	// Helper methods for template
	get isProductSelected(): boolean {
		return !!this.selectedProduct && !!this.selectedProductDetails;
	}

	get selectedPlatformName(): string {
		return this.catalog.selectedPlatformName;
	}

	get selectedProductName(): string {
		return this.catalog.selectedProductName;
	}

	get canSubmitForm(): boolean {
		// For unauthenticated users, just check if product is selected
		if (!this.isAuthenticated) {
			return !!this.selectedProduct;
		}

		if (this.requiresCustomComments) {
			const hasComments = this.getCommentsCount() > 0;
			const linkControl = this.boostForm.get('link');
			const hasValidLink = linkControl?.valid ?? false;
			return hasComments && hasValidLink;
		}

		return this.boostForm.valid;
	}

	calculatePrice(): number {
		// Don't calculate price for unauthenticated users
		if (!this.isAuthenticated) {
			return 0;
		}

		// If promo discount is active, return discounted price
		if (this.pricing.discount) {
			return this.pricing.discount.finalPrice;
		}

		// Only the API's price: until it answers (or if it failed) there is none
		return this.pricing.price > 0 ? this.pricing.price : 0;
	}

	/** Retry button of the price error */
	retryPrice(): void {
		const quantity = this.quantity;
		if (quantity) this.calculateDynamicPrice(quantity);
	}

	private calculateDynamicPrice(quantity: number): void {
		if (!this.selectedProductDetails || !quantity || quantity <= 0 || !this.isAuthenticated) {
			return;
		}

		const productIdNumber = parseInt(this.selectedProductDetails.id.toString());
		const quantityNumber = Number(quantity);

		if (Number.isNaN(productIdNumber) || !Number.isInteger(quantityNumber) || quantityNumber <= 0) {
			return;
		}

		// Only the latest request counts; with the price, the discount (automatic or promo code) it gets
		this.pricing.quote(productIdNumber, quantityNumber, (price) => this.pricing.previewDiscount(price));
	}

	applyPromoCode(code: string): void {
		this.pricing.applyPromoCode(code, (result) => {
			if (result === 'invalid') {
				this.messageAlertService.showMessage(
					'warn',
					this.translocoService.translate('boost.messages.promoInvalidTitle'),
					this.translocoService.translate('boost.messages.promoInvalid'),
				);
			} else if (result === 'error') {
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('boost.messages.promoVerifyError'),
				);
			}
		});
	}

	removePromoCode(): void {
		this.pricing.removePromoCode();
	}

	get requiresCustomComments(): boolean {
		return this.selectedProductDetails?.requiresCustomComments || false;
	}

	openCommentsDialog(): void {
		// Only allow for authenticated users
		if (!this.isAuthenticated) {
			this.saveFormDataToSession();
			this.router.navigate(['/login'], {
				queryParams: { returnUrl: '/' },
			});
			return;
		}
		this.showCommentsDialog = true;
	}

	saveComments(): void {
		const comments = this.getValidComments();

		if (comments.length === 0) {
			this.messageAlertService.showMessage(
				'warn',
				this.translocoService.translate('common.labels.warning'),
				this.translocoService.translate('boost.messages.commentRequired'),
			);
			return;
		}

		this.boostForm.patchValue({ quantity: comments.length });

		const quantityControl = this.boostForm.get('quantity');
		quantityControl?.markAsTouched();
		quantityControl?.updateValueAndValidity();

		this.showCommentsDialog = false;
	}

	getCommentsCount(): number {
		return this.getValidComments().length;
	}

	private getValidComments(): string[] {
		if (!this.customComments) return [];
		return this.customComments
			.split('\n')
			.map((line) => line.trim())
			.filter((line) => line.length > 0);
	}

	onCommentsChange(comments: string): void {
		this.customComments = comments;

		if (this.requiresCustomComments && this.isAuthenticated) {
			const commentsCount = this.getCommentsCount();
			if (commentsCount > 0 && this.selectedProductDetails) {
				this.calculateDynamicPrice(commentsCount);
			}
		}
	}

	// Additional getters for template
	get platformName(): string {
		return this.selectedPlatformName;
	}

	get productName(): string {
		return this.selectedProductName;
	}

	get linkPlaceholder(): string {
		return platformLinkPlaceholder(this.selectedPlatformName);
	}

	get quantity(): number {
		return this.requiresCustomComments
			? this.getCommentsCount()
			: Number(this.boostForm.get('quantity')?.value) || 0;
	}

	get minAmount(): number {
		return this.selectedProductDetails?.minAmount || 1;
	}

	get maxAmount(): number | null {
		return this.selectedProductDetails?.maxAmount || null;
	}

	// Button label for submit
	get submitButtonLabel(): string {
		if (!this.isAuthenticated) {
			return this.translocoService.translate('boost.submitButton.public');
		}
		return this.checkout.creatingOrder
			? this.translocoService.translate('boost.submitButton.processing')
			: this.translocoService.translate('boost.submitButton.submit');
	}

	// Open login modal
	goToLogin(): void {
		this.authModalService.openLogin();
	}

	// Open register modal
	goToRegister(): void {
		this.authModalService.openRegister();
	}
}
